import routes, { modalRoutes } from 'constants/routes';
import { useNavigate } from 'react-router-dom';
import { createSearchFromParams } from 'utils/url';
import { PostsAddNewProfileReason } from 'app/modules/posts/components/postsAddNewProfileModal/hooks/useAddNewProfileReasonMessages';
import { useUser } from 'modules/user/userSelector';
import { usePage } from 'modules/page/pageSelector';
import { useLocation } from 'react-router';
import { usePostsLayoutState } from './usePostsLayoutState';

type HistoryUrl =
  | string
  | { pathname?: string; hash?: string; search?: string };

type CreateInputParams = {
  date?: string;
  id?: number;
  pageId?: number;
  text?: string;
};

type CreateNewParams = { date?: string; text?: string };

export type PostsCreateNew = {
  createPost(params?: CreateNewParams): void;
  getCreatePostUrl(params?: CreateNewParams): HistoryUrl;
  createPostNew(params?: { id?: number; date?: string }): void;
  getCreatePostNewUrl(params?: { id?: number; date?: string }): HistoryUrl;
  createInspiration(params?: CreateNewParams): void;
  getCreateInspirationUrl(params?: CreateNewParams): HistoryUrl;
  isInPostsModule(): boolean;
};

export const usePostsCreateNew = (): PostsCreateNew => {
  const { lastPostsQueryParams } = useUser();
  const { pages, profileGroups } = usePage();
  const location = useLocation();
  const navigate = useNavigate();
  const isNewPostExperienceActive = usePostsLayoutState(
    (state) => state.isNewPostExperienceActive,
  );

  function isInPostsModule() {
    return !!location.pathname?.startsWith(routes.POSTS);
  }

  function getProfileIds() {
    const profileGroupsProfileIds = profileGroups
      .filter((group) => lastPostsQueryParams.profileGroups?.includes(group.id))
      .flatMap((group) => group.pages)
      .map((page) => page.id);

    const lastProfiles =
      [...lastPostsQueryParams?.profiles, ...profileGroupsProfileIds] ?? [];
    return lastProfiles.length > 0 && pages.length > 0
      ? lastProfiles
      : pages.map((page) => page.id);
  }

  function getRouteToCreate(toModal: string, params?: CreateNewParams) {
    const isInPosts = isInPostsModule();
    const profileIds = getProfileIds();

    if (profileIds.length === 0) {
      return {
        hash: modalRoutes.NEW_PROFILE,
        pathname: isInPosts ? undefined : routes.POSTS_CALENDAR,
        search: `?reason=${PostsAddNewProfileReason.noProfile}`,
      };
    }

    if (profileIds.length === 1) {
      return {
        hash: toModal,
        pathname: isInPosts ? undefined : routes.POSTS_CALENDAR,
        search: createSearchFromParams({
          compose: true,
          date: params?.date,
          text: params?.text,
          profileIds,
        }),
      };
    }

    return {
      hash: modalRoutes.SELECT_SINGLE_PROFILE_BEFORE_COMPOSE,
      pathname: isInPosts ? undefined : routes.POSTS_CALENDAR,
      search: createSearchFromParams({
        continueTo: toModal,
        profileIds,
        date: params?.date,
        text: params?.text,
      }),
    };
  }

  function getRouteToCreateNew(toModal: string, params?: CreateInputParams) {
    const isInPosts = isInPostsModule();
    const profileIds = getProfileIds();

    if (profileIds.length === 0) {
      return {
        hash: modalRoutes.NEW_PROFILE,
        pathname: isInPosts ? undefined : routes.POSTS_CALENDAR,
        search: `?reason=${PostsAddNewProfileReason.noProfile}`,
      };
    }

    if (profileIds.length === 1) {
      return {
        hash: toModal,
        pathname: isInPosts ? undefined : routes.POSTS_CALENDAR,
        search: createSearchFromParams({
          date: params?.date,
          id: params?.id,
          text: params?.text,
          pageId: params?.pageId ?? profileIds[0],
        }),
      };
    }

    return {
      hash: modalRoutes.SELECT_SINGLE_PROFILE_BEFORE_COMPOSE,
      pathname: isInPosts ? undefined : routes.POSTS_CALENDAR,
      search: createSearchFromParams({
        continueTo: toModal,
        profileIds,
        date: params?.date,
        id: params?.id,
        text: params?.text,
      }),
    };
  }

  function getCreatePostUrl(params?: CreateNewParams) {
    if (isNewPostExperienceActive) {
      return getRouteToCreateNew(modalRoutes.POST_DETAIL_NEW, params);
    }
    return getRouteToCreate(modalRoutes.POST_DETAIL, params);
  }

  function createPost(params?: CreateNewParams) {
    navigate(getCreatePostUrl(params));
  }

  function getCreatePostNewUrl(params?: { id?: number; date?: string }) {
    return getRouteToCreateNew(modalRoutes.POST_DETAIL_NEW, params);
  }

  function createPostNew(params?: { id?: number; date?: string }) {
    navigate(getCreatePostNewUrl(params));
  }

  function getCreateInspirationUrl(params?: CreateNewParams) {
    return getRouteToCreate(modalRoutes.INSPIRATION_DETAIL, params);
  }

  function createInspiration(params?: CreateNewParams) {
    navigate(getCreateInspirationUrl(params));
  }
  return {
    createPost,
    getCreatePostUrl,
    createPostNew,
    getCreatePostNewUrl,
    createInspiration,
    getCreateInspirationUrl,
    isInPostsModule,
  };
};
